/**
 * Security event tracking service
 * Logs security-relevant events for audit and incident response
 */

import { logger } from './logger'
import { User } from 'firebase/auth'

export type SecurityEventType =
  | 'auth_success'
  | 'auth_failure'
  | 'logout'
  | 'role_loaded'
  | 'role_changed'
  | 'role_mismatch'
  | 'token_refresh'
  | 'token_error'
  | 'unauthorized_access'
  | 'permission_denied'
  | 'session_expired'

interface SecurityEventContext {
  uid?: string
  role?: string
  previousRole?: string
  route?: string
  action?: string
  reason?: string
  [key: string]: string | undefined
}

class SecurityService {
  /**
   * Log security event
   */
  logEvent(eventType: SecurityEventType, context?: SecurityEventContext) {
    logger.security(eventType, {
      eventType,
      timestamp: new Date().toISOString(),
      ...context,
    })
  }

  /**
   * Log authentication success
   */
  logAuthSuccess(user: User, method?: string) {
    this.logEvent('auth_success', {
      uid: user.uid,
      method: method || 'unknown',
      provider: user.providerData[0]?.providerId,
    })
  }

  /**
   * Log authentication failure
   */
  logAuthFailure(reason: string, email?: string) {
    this.logEvent('auth_failure', {
      reason,
      // Email partially redacted in production automatically by logger
      email: email,
    })
  }

  /**
   * Log logout
   */
  logLogout(uid?: string) {
    this.logEvent('logout', { uid })
  }

  /**
   * Log role loaded
   */
  logRoleLoaded(uid: string, role: string) {
    this.logEvent('role_loaded', { uid, role })
  }

  /**
   * Log role changed
   */
  logRoleChanged(uid: string, previousRole: string, newRole: string) {
    this.logEvent('role_changed', {
      uid,
      previousRole,
      newRole,
    })
  }

  /**
   * Log role mismatch (potential tampering)
   */
  logRoleMismatch(uid: string, tokenRole: string, firestoreRole: string) {
    this.logEvent('role_mismatch', {
      uid,
      tokenRole,
      firestoreRole,
      severity: 'HIGH',
    })
  }

  /**
   * Log unauthorized access attempt
   */
  logUnauthorizedAccess(
    route: string,
    requiredRole?: string,
    actualRole?: string
  ) {
    this.logEvent('unauthorized_access', {
      route,
      requiredRole,
      actualRole,
    })
  }

  /**
   * Log permission denied
   */
  logPermissionDenied(action: string, reason: string) {
    this.logEvent('permission_denied', {
      action,
      reason,
    })
  }

  /**
   * Log token refresh
   */
  logTokenRefresh(uid: string, reason: 'proactive' | 'expiry' | 'manual') {
    this.logEvent('token_refresh', {
      uid,
      reason,
    })
  }

  /**
   * Log token error
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  logTokenError(uid: string, error: any) {
    this.logEvent('token_error', {
      uid,
      error: error?.code || 'unknown',
    })
  }
}

export const securityService = new SecurityService()
